import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, FileText, Briefcase, Download, X, Plus, Trash2, Pencil, MessageSquare, CheckCircle2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, getAdminToken, setAdminToken, API } from "@/lib/api";

const inputCls = "w-full border-2 border-slate-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-brand-orange";

const STATUSES = ["new", "reviewed", "completed", "rejected"];
const CATS = ["Industrial", "Technical", "Clerical", "Construction"];

const ACK_FIELDS = ["agreed_substance", "agreed_safety", "agreed_harassment", "agreed_general", "agreed_workcomp", "agreed_background", "agreed_waiver", "agreed_certification"];
const ACK_TOTAL = ACK_FIELDS.length;

function ackCount(a) {
    return ACK_FIELDS.reduce((n, f) => n + (a?.[f] ? 1 : 0), 0);
}

function AckPill({ a }) {
    const n = ackCount(a);
    const full = n === ACK_TOTAL;
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded-sm border ${full ? "bg-orange-50 text-brand-orange border-brand-orange" : "bg-slate-100 text-slate-500 border-slate-200"}`}
            data-testid="ack-pill"
            title={full ? "All policies acknowledged" : `${n} of ${ACK_TOTAL} policies acknowledged`}
        >
            {full && <CheckCircle2 className="h-3 w-3" />}
            {n} / {ACK_TOTAL}
        </span>
    );
}

function StatusBadge({ s }) {
    const map = { new: "bg-brand-orange text-white", reviewed: "bg-blue-100 text-blue-800", completed: "bg-green-100 text-green-800", rejected: "bg-slate-200 text-slate-600" };
    return <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded-sm ${map[s] || "bg-slate-200"}`}>{s}</span>;
}

export default function AdminDashboard() {
    const [tab, setTab] = useState("applications");
    const [apps, setApps] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [messages, setMessages] = useState([]);
    const [openApp, setOpenApp] = useState(null);
    const [jobEdit, setJobEdit] = useState(null);
    const [appsQuery, setAppsQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const nav = useNavigate();

    function logout() { setAdminToken(null); nav("/admin/login"); }

    async function loadApps() {
        try { const r = await api.get("/admin/applications"); setApps(r.data); }
        catch { toast.error("Failed to load applications"); }
    }
    async function loadJobs() {
        try { const r = await api.get("/jobs"); setJobs(r.data); }
        catch { toast.error("Failed to load jobs"); }
    }
    async function loadMessages() {
        try { const r = await api.get("/admin/contact-messages"); setMessages(r.data); }
        catch { toast.error("Failed to load messages"); }
    }

    useEffect(() => {
        loadApps(); loadJobs(); loadMessages();
    }, []);

    async function updateStatus(id, status) {
        try {
            await api.patch(`/admin/applications/${id}`, { status });
            toast.success(`Marked ${status}`);
            loadApps();
            if (openApp?.id === id) setOpenApp({ ...openApp, status });
        } catch { toast.error("Update failed"); }
    }

    async function saveNotes(id, recruiter_notes) {
        try {
            await api.patch(`/admin/applications/${id}`, { recruiter_notes });
            toast.success("Notes saved");
            loadApps();
            if (openApp?.id === id) setOpenApp({ ...openApp, recruiter_notes });
        } catch { toast.error("Failed to save notes"); }
    }

    async function saveJob(form) {
        try {
            if (jobEdit?.id) {
                await api.put(`/admin/jobs/${jobEdit.id}`, form);
                toast.success("Job updated");
            } else {
                await api.post("/admin/jobs", form);
                toast.success("Job created");
            }
            setJobEdit(null);
            loadJobs();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    }
    async function deleteJob(id) {
        if (!window.confirm("Delete this job posting?")) return;
        try { await api.delete(`/admin/jobs/${id}`); toast.success("Deleted"); loadJobs(); }
        catch { toast.error("Delete failed"); }
    }

    function buildSearchBlob(a) {
        const skills = Object.keys(a.skills || {}).filter((s) => a.skills[s]).join(" ");
        const addJobs = (a.additional_jobs || []).map((j) => [j.employer, j.title, j.duties, j.separation_reason].filter(Boolean).join(" ")).join(" ");
        return [
            a.full_name, a.email, a.phone, a.alt_phone,
            a.city, a.state, a.zipcode, a.address,
            a.applied_job_title, a.applied_job_category,
            a.high_school, a.college, a.course_of_study,
            a.recent_employer, a.recent_job_title, a.recent_duties, a.recent_separation_reason,
            addJobs, skills,
            a.recruiter_notes,
            a.status,
        ].filter(Boolean).join(" ").toLowerCase();
    }

    const filteredApps = (() => {
        let list = apps;
        if (statusFilter !== "all") list = list.filter((a) => (a.status || "new") === statusFilter);
        if (appsQuery.trim()) {
            const q = appsQuery.trim().toLowerCase();
            list = list.filter((a) => buildSearchBlob(a).includes(q));
        }
        return list;
    })();

    const statusCounts = STATUSES.reduce((acc, s) => {
        acc[s] = apps.filter((a) => (a.status || "new") === s).length;
        return acc;
    }, { all: apps.length });

    function downloadResume(appId) {
        const token = getAdminToken();
        window.open(`${API}/admin/applications/${appId}/resume?auth=${encodeURIComponent(token)}`, "_blank");
    }

    function downloadApplicationPdf(appId) {
        const token = getAdminToken();
        window.open(`${API}/admin/applications/${appId}/pdf?auth=${encodeURIComponent(token)}`, "_blank");
    }

    function downloadApplicationFullPdf(appId) {
        const token = getAdminToken();
        window.open(`${API}/admin/applications/${appId}/pdf/full?auth=${encodeURIComponent(token)}`, "_blank");
    }

    return (
        <div className="min-h-screen bg-slate-50 flex" data-testid="admin-dashboard">
            <aside className="w-60 bg-brand-navy text-white min-h-screen p-4 flex flex-col">
                <Link to="/" className="font-heading font-black uppercase text-lg tracking-tight">Tower Staffing<div className="text-[10px] text-slate-400 tracking-wider">Admin</div></Link>
                <nav className="mt-8 space-y-1 flex-1">
                    {[
                        { id: "applications", Icon: FileText, label: "Applications", count: apps.length },
                        { id: "jobs", Icon: Briefcase, label: "Jobs", count: jobs.length },
                        { id: "messages", Icon: MessageSquare, label: "Messages", count: messages.length },
                    ].map((t) => (
                        <button key={t.id} onClick={() => setTab(t.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-heading uppercase tracking-wider font-bold transition-colors ${tab === t.id ? "bg-brand-orange text-white" : "text-slate-300 hover:bg-white/5"}`} data-testid={`tab-${t.id}`}>
                            <t.Icon className="h-4 w-4" />{t.label} <span className="ml-auto text-xs opacity-70">{t.count}</span>
                        </button>
                    ))}
                </nav>
                <button onClick={logout} className="flex items-center gap-2 text-slate-300 hover:text-brand-orange text-sm font-bold px-3 py-2" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" />Sign Out</button>
            </aside>

            <main className="flex-1 p-6 md:p-10">
                {tab === "applications" && (
                    <>
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <h1 className="font-heading text-3xl font-black uppercase text-brand-navy">Applications</h1>
                            <div className="relative flex-1 max-w-md min-w-[240px]">
                                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    value={appsQuery}
                                    onChange={(e) => setAppsQuery(e.target.value)}
                                    placeholder="Search name, email, skills, employer, duties, notes..."
                                    className="w-full pl-9 pr-9 py-2 border-2 border-slate-200 focus:border-brand-orange rounded-sm text-sm focus:outline-none bg-white"
                                    data-testid="apps-search-input"
                                />
                                {appsQuery && (
                                    <button onClick={() => setAppsQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-brand-orange" data-testid="apps-search-clear">
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                        {appsQuery.trim() && (
                            <div className="mt-3 text-xs uppercase tracking-wider font-bold text-slate-500" data-testid="apps-search-summary">
                                {filteredApps.length} of {apps.length} matching &ldquo;<span className="text-brand-orange">{appsQuery}</span>&rdquo;
                            </div>
                        )}

                        <div className="mt-5 flex flex-wrap gap-2" data-testid="status-tabs">
                            {[{ id: "all", label: "All" }, ...STATUSES.map((s) => ({ id: s, label: s }))].map((t) => {
                                const active = statusFilter === t.id;
                                const count = statusCounts[t.id] ?? 0;
                                return (
                                    <button
                                        key={t.id}
                                        onClick={() => setStatusFilter(t.id)}
                                        className={`px-3 py-1.5 font-heading uppercase tracking-wider text-[11px] font-bold border-2 rounded-sm transition-colors inline-flex items-center gap-2 ${active ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-brand-navy border-slate-200 hover:border-brand-orange"}`}
                                        data-testid={`status-tab-${t.id}`}
                                    >
                                        {t.label}
                                        <span className={`inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-[10px] font-bold rounded-sm ${active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"}`}>{count}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-6 bg-white border-2 border-slate-200 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-bold">
                                    <tr>
                                        <th className="text-left px-4 py-3">Applicant</th>
                                        <th className="text-left px-4 py-3">Submitted</th>
                                        <th className="text-left px-4 py-3">Status</th>
                                        <th className="text-left px-4 py-3">Policies</th>
                                        <th className="text-left px-4 py-3">Resume</th>
                                        <th className="text-right px-4 py-3">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredApps.length === 0 && <tr><td colSpan="6" className="px-4 py-12 text-center text-slate-400">{appsQuery.trim() ? "No applications match your search." : "No applications yet."}</td></tr>}
                                    {filteredApps.map((a) => (
                                        <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`app-row-${a.id}`}>
                                            <td className="px-4 py-3">
                                                <div className="font-bold text-brand-navy">{a.full_name}</div>
                                                <div className="text-xs text-slate-500">{a.email} · {a.phone}</div>
                                                {a.applied_job_title ? (
                                                    <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 border border-brand-orange/30 rounded-sm text-[10px] uppercase tracking-wider font-bold text-brand-orange" title={`Applied to ${a.applied_job_title} (${a.applied_job_category})`} data-testid={`applied-to-${a.id}`}>
                                                        <Briefcase className="h-3 w-3" />{a.applied_job_title}
                                                    </div>
                                                ) : (
                                                    <div className="mt-1 inline-block px-2 py-0.5 bg-slate-100 rounded-sm text-[10px] uppercase tracking-wider font-bold text-slate-500">General Application</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-slate-500 text-xs">{new Date(a.submitted_at).toLocaleString()}</td>
                                            <td className="px-4 py-3"><StatusBadge s={a.status} /></td>
                                            <td className="px-4 py-3"><AckPill a={a} /></td>
                                            <td className="px-4 py-3">{a.resume_path ? <button onClick={() => downloadResume(a.id)} className="text-brand-orange font-bold inline-flex items-center gap-1 text-xs"><Download className="h-3 w-3" />Download</button> : <span className="text-xs text-slate-400">—</span>}</td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="inline-flex items-center gap-3">
                                                    <button onClick={() => downloadApplicationPdf(a.id)} className="text-xs font-heading uppercase tracking-wider font-bold text-brand-orange hover:text-brand-navy inline-flex items-center gap-1" data-testid={`pdf-summary-${a.id}`} title="Download 1-page summary PDF"><Download className="h-3 w-3" />Summary</button>
                                                    <button onClick={() => downloadApplicationFullPdf(a.id)} className="text-xs font-heading uppercase tracking-wider font-bold text-brand-navy hover:text-brand-orange inline-flex items-center gap-1" data-testid={`pdf-full-${a.id}`} title="Download full application PDF (all Q&A + signed policies)"><Download className="h-3 w-3" />Full</button>
                                                    <button onClick={() => setOpenApp(a)} className="text-xs font-heading uppercase tracking-wider font-bold text-brand-navy hover:text-brand-orange" data-testid={`view-app-${a.id}`}>View</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {tab === "jobs" && (
                    <>
                        <div className="flex items-center justify-between">
                            <h1 className="font-heading text-3xl font-black uppercase text-brand-navy">Job Postings</h1>
                            <button onClick={() => setJobEdit({ title: "", category: "Industrial", location: "Fort Wayne, IN", pay_rate: "", shift: "", employment_type: "Full-time", description: "", requirements: [], is_active: true })} className="inline-flex items-center gap-2 bg-brand-orange text-white font-heading uppercase tracking-wider font-bold text-xs px-4 py-2 rounded-sm" data-testid="new-job-btn">
                                <Plus className="h-4 w-4" />New Job
                            </button>
                        </div>
                        <div className="mt-6 grid md:grid-cols-2 gap-4">
                            {jobs.map((j) => (
                                <div key={j.id} className="bg-white border-2 border-slate-200 p-5" data-testid={`admin-job-${j.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-orange">{j.category}</div>
                                            <div className="font-heading text-xl font-black uppercase text-brand-navy mt-1">{j.title}</div>
                                            <div className="text-sm text-slate-600 mt-1">{j.location} · {j.shift} · {j.pay_rate}</div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => setJobEdit(j)} className="text-slate-500 hover:text-brand-orange" data-testid={`edit-job-${j.id}`}><Pencil className="h-4 w-4" /></button>
                                            <button onClick={() => deleteJob(j.id)} className="text-slate-500 hover:text-red-600" data-testid={`delete-job-${j.id}`}><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {tab === "messages" && (
                    <>
                        <h1 className="font-heading text-3xl font-black uppercase text-brand-navy">Contact Messages</h1>
                        <div className="mt-6 space-y-3">
                            {messages.length === 0 && <div className="text-slate-400">No messages.</div>}
                            {messages.map((m) => (
                                <div key={m.id} className="bg-white border-2 border-slate-200 p-5">
                                    <div className="flex items-center justify-between">
                                        <div className="font-bold text-brand-navy">{m.name} <span className="font-normal text-slate-500 text-sm">· {m.email}{m.phone && ` · ${m.phone}`}</span></div>
                                        <div className="text-xs text-slate-400">{new Date(m.received_at).toLocaleString()}</div>
                                    </div>
                                    {m.subject && <div className="mt-1 text-sm font-bold text-slate-700">{m.subject}</div>}
                                    <div className="mt-2 text-slate-700 whitespace-pre-line">{m.message}</div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </main>

            {/* Application detail modal */}
            {openApp && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpenApp(null)}>
                    <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="application-detail-modal">
                        <div className="sticky top-0 bg-white border-b-2 border-slate-100 px-6 py-4 flex items-center justify-between">
                            <div>
                                <div className="font-heading text-2xl font-black uppercase text-brand-navy">{openApp.full_name}</div>
                                <div className="text-xs text-slate-500">Submitted {new Date(openApp.submitted_at).toLocaleString()}</div>
                            </div>
                            <button onClick={() => setOpenApp(null)} className="text-slate-500"><X /></button>
                        </div>
                        <div className="p-6 space-y-5 text-sm">
                            <div className="flex flex-wrap gap-2 items-center">
                                <StatusBadge s={openApp.status} />
                                {STATUSES.map((s) => (
                                    <button key={s} onClick={() => updateStatus(openApp.id, s)} className={`px-3 py-1 text-xs font-heading uppercase tracking-wider font-bold border-2 rounded-sm ${openApp.status === s ? "border-brand-navy bg-brand-navy text-white" : "border-slate-200 hover:border-brand-orange"}`} data-testid={`set-status-${s}`}>{s}</button>
                                ))}
                                {openApp.resume_path && <button onClick={() => downloadResume(openApp.id)} className="ml-auto inline-flex items-center gap-1 text-brand-orange font-bold text-xs"><Download className="h-3 w-3" />Download Resume</button>}
                                <button onClick={() => downloadApplicationPdf(openApp.id)} className={`${openApp.resume_path ? "" : "ml-auto"} inline-flex items-center gap-1 bg-brand-navy hover:bg-brand-orange text-white font-heading uppercase tracking-wider font-bold text-[10px] px-3 py-1.5 rounded-sm transition-colors`} data-testid="download-summary-pdf-modal-btn" title="1-page recruiter summary"><Download className="h-3 w-3" />Summary PDF</button>
                                <button onClick={() => downloadApplicationFullPdf(openApp.id)} className="inline-flex items-center gap-1 bg-brand-orange hover:bg-brand-navy text-white font-heading uppercase tracking-wider font-bold text-[10px] px-3 py-1.5 rounded-sm transition-colors" data-testid="download-full-pdf-modal-btn" title="Full printout — all Q&A + signed policies"><Download className="h-3 w-3" />Full PDF</button>
                            </div>

                            <RecruiterNotes app={openApp} onSave={saveNotes} />

                            <Section title="Application Source">
                                <KV k="Applied To" v={openApp.applied_job_title ? `${openApp.applied_job_title}${openApp.applied_job_category ? ` (${openApp.applied_job_category})` : ""}` : "General Application (no specific job)"} />
                                <KV k="Source URL" v={openApp.apply_source_url} />
                                <KV k="Referrer" v={openApp.referrer_url || "Direct / Bookmark"} />
                            </Section>
                            <Section title="Personal">
                                <KV k="Email" v={openApp.email} />
                                <KV k="Phone" v={openApp.phone} />
                                <KV k="Alt Phone" v={openApp.alt_phone} />
                                <KV k="DOB" v={openApp.date_of_birth} />
                                <KV k="SSN" v={openApp.ssn} />
                                <KV k="Address" v={`${openApp.address}, ${openApp.city} ${openApp.state} ${openApp.zipcode}`} />
                            </Section>
                            <Section title="Position & Availability">
                                <KV k="Employment Type" v={(openApp.employment_types || []).join(", ")} />
                                <KV k="Shifts" v={(openApp.shifts || []).join(", ")} />
                                <KV k="Min Wage" v={openApp.min_wage} />
                                <KV k="Notice" v={openApp.notice_required} />
                                <KV k="Can Work" v={(openApp.can_work || []).join(", ")} />
                                <KV k="Currently Working" v={openApp.currently_working} />
                                <KV k="Transportation" v={openApp.transportation} />
                                <KV k="Driver's License" v={openApp.has_drivers_license} />
                                <KV k="PPE Owned" v={(openApp.ppe_owned || []).join(", ")} />
                            </Section>
                            <Section title="Eligibility">
                                <KV k="18+" v={openApp.age_18_plus} />
                                <KV k="US Work Eligible" v={openApp.legally_eligible} />
                                <KV k="Used Tower Before" v={openApp.used_tower_before} />
                                <KV k="Convictions" v={openApp.has_convictions === "Yes" ? `Yes — ${openApp.convictions_detail}` : "No"} />
                                <KV k="Can Lift 50lbs / Stand 8hr" v={openApp.has_restrictions === "No" ? `No — ${openApp.restrictions_detail}` : "Yes"} />
                                <KV k="Future Conflicts" v={openApp.future_conflicts === "Yes" ? `Yes — ${openApp.conflicts_detail}` : "No"} />
                            </Section>
                            <Section title="Experience">
                                <KV k="High School" v={`${openApp.high_school} (${openApp.high_school_grad})`} />
                                <KV k="GED" v={openApp.has_ged} />
                                <KV k="College" v={openApp.college} />
                                <KV k="Course" v={openApp.course_of_study} />
                                <KV k="Recent Employer" v={`${openApp.recent_employer || ""} · ${openApp.recent_job_title || ""}`} />
                                <KV k="Additional Employers" v={openApp.additional_employers} />
                                <KV k="Skills" v={Object.keys(openApp.skills || {}).filter((s) => openApp.skills[s]).join(" · ") || "—"} />
                            </Section>
                            <Section title="Aptitude">
                                <KV k="Can Read Tape Measure" v={openApp.can_read_tape} />
                                <KV k="Tape Q1 (1/4)" v={openApp.tape_q1} />
                                <KV k="Tape Q2 (1/8)" v={openApp.tape_q2} />
                                <KV k="Tape Q3 (1/16)" v={openApp.tape_q3} />
                            </Section>
                            <Section title="Signature & Acknowledgements">
                                <KV k="Initials" v={openApp.signature_initials} />
                                <KV k="Signed At" v={openApp.submitted_at ? new Date(openApp.submitted_at).toLocaleString() : ""} />
                                <KV k="Signer IP" v={openApp.signed_ip} />
                                <KV k="Browser / Device" v={openApp.signed_user_agent ? (openApp.signed_user_agent.length > 90 ? openApp.signed_user_agent.slice(0, 90) + "…" : openApp.signed_user_agent) : "—"} />
                                <KV k="Acknowledgements" v={`${ackCount(openApp)} / ${ACK_TOTAL}`} />
                                <KV k="Substance Abuse" v={openApp.agreed_substance ? "Agreed" : "—"} />
                                <KV k="Health & Safety" v={openApp.agreed_safety ? "Agreed" : "—"} />
                                <KV k="Harassment" v={openApp.agreed_harassment ? "Agreed" : "—"} />
                                <KV k="General Employment" v={openApp.agreed_general ? "Agreed" : "—"} />
                                <KV k="Workers' Comp Safety" v={openApp.agreed_workcomp ? "Agreed" : "—"} />
                                <KV k="Background Check" v={openApp.agreed_background ? "Agreed" : "—"} />
                                <KV k="Benefits Waiver" v={openApp.agreed_waiver ? "Agreed" : "—"} />
                                <KV k="App Certification" v={openApp.agreed_certification ? "Agreed" : "—"} />
                                <KV k="Marketing/SMS Opt-In" v={openApp.marketing_consent ? "Opted In" : "Declined"} />
                            </Section>
                        </div>
                    </div>
                </div>
            )}

            {/* Job edit modal */}
            {jobEdit && <JobEditor job={jobEdit} onClose={() => setJobEdit(null)} onSave={saveJob} />}
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <div className="font-heading text-xs uppercase tracking-[0.2em] font-bold text-brand-orange border-b border-slate-200 pb-1 mb-2">{title}</div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-slate-700">{children}</div>
        </div>
    );
}
function KV({ k, v }) {
    return (<div><span className="font-bold text-slate-500 text-xs uppercase tracking-wider mr-2">{k}:</span>{v || <em className="text-slate-400">—</em>}</div>);
}

function RecruiterNotes({ app, onSave }) {
    const [val, setVal] = useState(app.recruiter_notes || "");
    const [saving, setSaving] = useState(false);
    useEffect(() => { setVal(app.recruiter_notes || ""); }, [app.id, app.recruiter_notes]);
    const dirty = (val || "") !== (app.recruiter_notes || "");
    async function commit() {
        setSaving(true);
        try { await onSave(app.id, val); } finally { setSaving(false); }
    }
    return (
        <div className="border-2 border-amber-200 bg-amber-50 rounded-sm p-4" data-testid="recruiter-notes-block">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <div className="font-heading uppercase tracking-wider text-xs font-bold text-brand-navy">Recruiter Notes <span className="ml-2 text-[10px] font-bold text-amber-700">PRIVATE · ADMIN ONLY</span></div>
                <button
                    type="button"
                    onClick={commit}
                    disabled={!dirty || saving}
                    className="bg-brand-navy hover:bg-brand-orange disabled:opacity-40 disabled:cursor-not-allowed text-white font-heading uppercase tracking-wider font-bold text-[10px] px-3 py-1.5 rounded-sm transition-colors"
                    data-testid="save-recruiter-notes-btn"
                >
                    {saving ? "Saving..." : dirty ? "Save Notes" : "Saved"}
                </button>
            </div>
            <textarea
                value={val}
                onChange={(e) => setVal(e.target.value)}
                rows={4}
                placeholder="Add private notes here — phone screen takeaways, placement ideas, references contacted, follow-up reminders. Visible to admins only, never shown to the applicant or included in PDF downloads."
                className="w-full border-2 border-amber-200 focus:border-brand-orange rounded-sm px-3 py-2 text-sm focus:outline-none bg-white"
                data-testid="recruiter-notes-textarea"
            />
        </div>
    );
}

function JobEditor({ job, onClose, onSave }) {
    const [f, setF] = useState({
        title: job.title || "", category: job.category || "Industrial", location: job.location || "Fort Wayne, IN",
        pay_rate: job.pay_rate || "", shift: job.shift || "", employment_type: job.employment_type || "Full-time",
        description: job.description || "", requirements: job.requirements || [], is_active: job.is_active !== false,
    });
    const [reqText, setReqText] = useState((job.requirements || []).join("\n"));
    function save() {
        const reqs = reqText.split("\n").map((s) => s.trim()).filter(Boolean);
        if (!f.title.trim() || !f.description.trim()) { toast.error("Title and description required"); return; }
        onSave({ ...f, requirements: reqs });
    }
    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="job-editor-modal">
                <div className="sticky top-0 bg-white border-b-2 border-slate-100 px-6 py-4 flex items-center justify-between">
                    <div className="font-heading text-xl font-black uppercase text-brand-navy">{job.id ? "Edit Job" : "New Job"}</div>
                    <button onClick={onClose} className="text-slate-500"><X /></button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <input className={inputCls} placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="job-edit-title" />
                        <select className={inputCls} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="job-edit-category">
                            {CATS.map((c) => <option key={c}>{c}</option>)}
                        </select>
                        <input className={inputCls} placeholder="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} />
                        <input className={inputCls} placeholder="Pay Rate (e.g. $18 - $22 / hr)" value={f.pay_rate} onChange={(e) => setF({ ...f, pay_rate: e.target.value })} />
                        <input className={inputCls} placeholder="Shift" value={f.shift} onChange={(e) => setF({ ...f, shift: e.target.value })} />
                        <input className={inputCls} placeholder="Employment Type" value={f.employment_type} onChange={(e) => setF({ ...f, employment_type: e.target.value })} />
                    </div>
                    <textarea className={inputCls} rows={4} placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="job-edit-description" />
                    <textarea className={inputCls} rows={4} placeholder="Requirements (one per line)" value={reqText} onChange={(e) => setReqText(e.target.value)} />
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} className="accent-brand-orange" /> Active (visible to applicants)</label>
                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={onClose} className="px-5 py-2 border-2 border-slate-200 rounded-sm font-heading uppercase tracking-wider font-bold text-xs">Cancel</button>
                        <button onClick={save} className="px-5 py-2 bg-brand-orange text-white rounded-sm font-heading uppercase tracking-wider font-bold text-xs" data-testid="job-editor-save-btn">Save</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
